import React, { useState } from 'react';
import { IPriceChange } from "../../interfaces/priceChange.interface";
import { Layout, Col, Table, Spin, Tooltip } from "antd";
import { CloseOutlined, BorderOutlined, CheckSquareOutlined, InteractionOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import Comments from "./cell/Comment"
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { FilterFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../reduxStore/reducers/rootReducer';
import '../../styles/responsive-table.css';

const { Content } = Layout;

const PriceChangeTable = (
    {
        priceChangeData,
        pdfSignClickHandler,
        genericAltSignClickHandler,
        commentsSubmitHandler,
        priceChangeOverrideHandler,
        store
    }: {
        priceChangeData: IPriceChange[],
        pdfSignClickHandler: Function,
        genericAltSignClickHandler: Function,
        commentsSubmitHandler: Function,
        priceChangeOverrideHandler: Function,
        store: string
    }) => {

    const readOnlyState = useSelector(
        (state: RootState) => state.readOnly
    )

    const signKitColorsState = useSelector(
        (state: RootState) => state.signKitColors.colors
    )

    const isFilterActive = useSelector(
        (state: RootState) => state.chainInventoryFilter.isFilterActive
    )

    const [pdfLoadingIds, setPdfLoadingIds] = useState<Set<string>>(new Set());
    const [altColorLoadingIds, setAltColorLoadingIds] = useState<Set<string>>(new Set());
    const [commentsLoadingIds, setCommentsLoadingIds] = useState<Set<string>>(new Set());
    const [priceChangeOverrideLoadingIds, setPriceChangeOverrideLoadingIds] = useState<Set<string>>(new Set());

    const filteredPriceChangeData = isFilterActive 
        ? priceChangeData.filter(item => {
            if (item.styles && Array.isArray(item.styles) && item.styles.length > 0) {
                return item.styles.some(style => 
                    style.inventory !== undefined && style.inventory >= 10
                );
            }
            return true;
          })
        : priceChangeData;

    const handlePdfClick = async (id: Number, is_pdf: boolean, stores: string[], is_alt_color: boolean) => {
        const key = String(id); 

        if (pdfLoadingIds.has(key)) {
            return; // Prevent multiple clicks while loading
        }

        setPdfLoadingIds(prev => new Set(prev).add(key));

        try {
            await pdfSignClickHandler(id, is_pdf, stores, is_alt_color);
        } catch (error) {
            console.error("PDF Sign update error:", error);
        } finally {
            setPdfLoadingIds(prev => {
                const updated = new Set(prev);
                updated.delete(key);
                return updated;
            });
        }
    };

    const handleAltColorClick = async (id: Number, is_alt_color: boolean, stores: string[]) => {
        const key = String(id); 

        if (altColorLoadingIds.has(key)) {
            return; // Prevent multiple clicks while loading
        }

        setAltColorLoadingIds(prev => new Set(prev).add(key));

        try {
            await genericAltSignClickHandler(id, is_alt_color, stores);
        } catch (error) {
            console.error("Alt Color update error:", error);
        } finally {
            setAltColorLoadingIds(prev => {
                const updated = new Set(prev);
                updated.delete(key);
                return updated;
            });
        }
    };

    const handleCommentsSubmit = async (base_id: Number, id: Number, comment: string, stores: string[]) => {
        const key = `${base_id}-${id}`;

        if (commentsLoadingIds.has(key)) {
            return; 
        }

        setCommentsLoadingIds(prev => new Set(prev).add(key));
        try {
            await commentsSubmitHandler(base_id, id, comment, stores);
        } catch (error) {
            console.error("Comment update error:", error);
        } finally {
            setCommentsLoadingIds(prev => {
                const updated = new Set(prev);
                updated.delete(key);
                return updated;
            });
        }
    };

    const handlePriceChangeOverrideToggle = async (id: Number, currentPriceChangeOverride: boolean, stores: string[]) => {
        const key = String(id);

        if (priceChangeOverrideLoadingIds.has(key)) {
            return; // Prevent multiple clicks while loading
        }

        setPriceChangeOverrideLoadingIds(prev => new Set(prev).add(key));

        try {
            await priceChangeOverrideHandler(id, currentPriceChangeOverride, stores);
        } catch (error) {
            console.error("Price change override error:", error);
        } finally {
            setPriceChangeOverrideLoadingIds(prev => {
                const updated = new Set(prev);
                updated.delete(key);
                return updated;
            });
        }
    };

    const getUniqueValuesComma = (data: any[], key: string | number) => {
        const uniqueValues = new Set();
        data.forEach(item => {
            if (Array.isArray(item[key])) {
                item[key].forEach((value: any) => uniqueValues.add(value));
            } else if (typeof item[key] === 'string') {
                item[key].split(',').forEach((value: string) => uniqueValues.add(value.trim()));
            } else {
                uniqueValues.add(item[key]);
            }
        });
        return Array.from(uniqueValues).map(value => ({
            text: value,
            value: value,
        }));
    };

    const columns: ColumnsType<IPriceChange> = [
        {
            title: "Brand",
            dataIndex: "brand",
            key: "brand",
            width: "8em",
            filters: getUniqueValuesComma(priceChangeData, 'brand') as FilterDropdownProps['filters'],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => a.brand.localeCompare(b.brand),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { brand }) => (
                brand.replace(',', ', ')
            ),
            onFilter: (value, record) => String(record.brand).split(',').map(v => v.trim()).includes(String(value)),
        },
        {
            title: "SBU",
            dataIndex: "category",
            key: "category",
            width: "8em",
            filters: getUniqueValuesComma(priceChangeData, 'category') as FilterDropdownProps['filters'],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => a.category.join(',').localeCompare(b.category.join(',')),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { category }) => (
                category.join(',')
            ),
            onFilter: (value, record) => String(record.category).split(',').map(v => v.trim()).includes(String(value)),
        },
        {
            title: "Department",
            dataIndex: "department",
            key: "department",
            width: "8em",
            filters: getUniqueValuesComma(priceChangeData, 'department') as FilterDropdownProps['filters'],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => a.department.join(',').localeCompare(b.department.join(',')),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { department }) => (
                department.join(',')
            ),
            onFilter: (value, record) => String(record.department).split(',').map(v => v.trim()).includes(String(value)),
        },
        {
            title: "Collection",
            dataIndex: "collection",
            key: "collection",
            width: "10em",
            filters: getUniqueValuesComma(priceChangeData, 'collection') as FilterDropdownProps['filters'],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => a.collection.localeCompare(b.collection),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            onFilter: (value, record) => record.collection.indexOf(String(value)) === 0,
        },
        {
            title: "Price Changed",
            dataIndex: "price_change",
            key: "price_change",
            align: "center",
            width: "10em",
            defaultSortOrder: 'descend',
            filters: [
                { text: 'Change', value: true },
                { text: 'No change', value: false },
            ],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => Number(a.price_change) - Number(b.price_change),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { price_change, price_change_override, id }) => {
                const key = String(id);
                const isLoading = priceChangeOverrideLoadingIds.has(key);
                const isOverridden = price_change_override === true;
                const displayedPriceChange = isOverridden ? !price_change : price_change;
                
                const handleOverrideToggle = async (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!readOnlyState.isReadOnly) {
                        await handlePriceChangeOverrideToggle(id, isOverridden, [store]);
                    }
                };

                return (
                    <div style={{ position: 'relative', display: 'inline-block', minWidth: '32px', minHeight: '32px' }}>
                        {isLoading ? (
                            <Spin size="small" />
                        ) : (
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }}>
                                {displayedPriceChange ? (
                                    <CloseOutlined style={{ "color": "red" }} />
                                ) : null}
                                <Tooltip title={isOverridden ? "Remove Price Change Override" : "Override Price Change"}>
                                    <InteractionOutlined 
                                        style={{ 
                                            "color": isOverridden ? "#52c41a" : "#1890ff",
                                            cursor: readOnlyState.isReadOnly ? 'default' : 'pointer',
                                            position: 'relative',
                                            bottom: '-12px',
                                            right: '-12px',
                                            fontSize: '10px'
                                        }}
                                        onClick={handleOverrideToggle}
                                    />
                                </Tooltip>
                            </div>
                        )}
                    </div>
                );
            },
            onFilter: (value, record) => record.price_change === value,
        },
        {
            title: "Promo Price",
            dataIndex: "promo_price",
            key: "promo_price",
            width: "10em",
            filters: getUniqueValuesComma(priceChangeData, 'promo_price') as FilterDropdownProps['filters'],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => a.promo_price.localeCompare(b.promo_price),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            onFilter: (value, record) => String(record.promo_price).split(',').map(v => v.trim()).includes(String(value)),
        },
        {
            title: "Sign Kit",
            dataIndex: "sign_kit",
            key: "sign_kit",
            width: "8em",
            filters: getUniqueValuesComma(priceChangeData, 'sign_kit') as FilterDropdownProps['filters'],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => a.sign_kit.localeCompare(b.sign_kit),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { is_alt_color, is_pdf, sign_kit }) => (
                <span style={{ color: (is_alt_color && (!is_pdf)) ? signKitColorsState.GenericAltColor : 'black' }}>
                    {is_pdf ? "PDF" : sign_kit}
                </span>
            ),
            onFilter: (value, record) => String(record.sign_kit).split(',').map(v => v.trim()).includes(String(value)),
        },
        {
            title: "Alt Color",
            dataIndex: "is_alt_color",
            key: "is_alt_color",
            align: "center",
            width: "6em",
            filters: [
                { text: 'Active', value: true },
                { text: 'Inactive', value: false },
            ],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => {
                if (a.is_alt_color === b.is_alt_color) return 0;
                return a.is_alt_color ? 1 : -1;
            },
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { is_alt_color, is_pdf, id }) => {
                const key = String(id);
                const isLoading = altColorLoadingIds.has(key);

                if (readOnlyState.isReadOnly) {
                    return is_alt_color ? <CheckSquareOutlined /> : <BorderOutlined />;
                }

                if (is_pdf) {
                    return <BorderOutlined style={{ color: 'gray', backgroundColor: 'lightgray', pointerEvents: 'none' }} />;
                }

                return (
                    <div style={{ position: "relative", display: "inline-block", width: "24px", height: "24px" }}>
                        {isLoading ? (
                            <Spin size="small" />
                        ) : (
                            is_alt_color ?
                                <CheckSquareOutlined onClick={() => handleAltColorClick(id, is_alt_color, [store])} /> :
                                <BorderOutlined onClick={() => handleAltColorClick(id, is_alt_color, [store])} />
                        )}
                    </div>
                );
            },
            onFilter: (value, record) => {
                try {
                    if (record.is_alt_color) {
                        return value === true;
                    } else {
                        return value === false;
                    }
                } catch (error) {
                    return false;
                }
            }
        },
        {
            title: "PDF Sign",
            dataIndex: "is_pdf",
            key: "is_pdf",
            align: "center",
            width: "6em",
            filters: [
                { text: 'Active', value: true },
                { text: 'Inactive', value: false },
            ],
            filterIcon: filtered => <FilterFilled style={{ color: filtered ? '#fff' : '#808080' }} />,
            sorter: (a: IPriceChange, b: IPriceChange) => {
                if (a.is_pdf === b.is_pdf) return 0;
                return a.is_pdf ? 1 : -1;
            },
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { is_pdf, id, is_alt_color }) => {
                const key = String(id);
                const isLoading = pdfLoadingIds.has(key);

                if (readOnlyState.isReadOnly) {
                    return is_pdf ? <CheckSquareOutlined /> : <BorderOutlined />;
                }

                return (
                    <div style={{ position: "relative", display: "inline-block", width: "24px", height: "24px" }}>
                        {isLoading ? (
                            <Spin size="small" />
                        ) : (
                            is_pdf ?
                                <CheckSquareOutlined onClick={() => { handlePdfClick(id, is_pdf, [store], is_alt_color) }} /> :
                                <BorderOutlined onClick={() => { handlePdfClick(id, is_pdf, [store], is_alt_color) }} />
                        )}
                    </div>
                );
            },
            onFilter: (value, record) => {
                try {
                    if (record.sign_kit === "PDF") {
                        return value === true;
                    } else {
                        return value === false;
                    }
                } catch (error) {
                    return false;
                }
            }
        },
        {
            title: "Comments",
            dataIndex: "comments",
            key: "comments",
            width: "20em",
            // filters: getUniqueValues(priceChangeData, 'comments') as FilterDropdownProps['filters'],
            sorter: (a: IPriceChange, b: IPriceChange) => a.comments.localeCompare(b.comments),
            onCell: (_record, _rowIndex) => {
                return {
                    style: { padding: "8px" }
                };
            },
            render: (_, { base_id, id, comments }) => {
                const key = `${base_id}-${id}`;
                const isLoading = commentsLoadingIds.has(key);

                return (
                    <Comments
                        baseId={base_id}
                        priceChangeId={id}
                        comments={comments}
                        commentsSubmitHandler={handleCommentsSubmit} 
                        stores={[store]}
                        isLoading={isLoading} // Pass loading state to Comments component
                    />
                );
            },
            // onFilter: (value, record) => record.comments.indexOf(String(value)) === 0,
        }
    ]

    const paginationConfig = {
        'defaultPageSize': 100,
        'showSizeChanger': true,
        'pageSizeOptions': [10, 25, 50, 100],
        'showExpandColumn': true
    }

    return (
        <Content
            style={{
                padding: "8px"
            }}
        >
            <Col offset={0} span={24}>
                <Table
                    className='price-change-table'
                    dataSource={filteredPriceChangeData}
                    columns={columns}
                    rowKey={(record) => record.id.toString()}
                    sticky
                    pagination={paginationConfig}
                />
            </Col>
        </Content>
    );
}

export default PriceChangeTable;